import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { formatAmountRound } from '../utils/formatAmount';
import useManageOrder from '../features/order/useManageOrder';
import { companyBankText } from '../utils/companyBank';
import DebtFollowUpService from '../features/debtFollowUp/DebtFollowUpService';

const PaymentTextArea = ({
  orders,
  orderSummary,
  smileName,
  advanceAmount = 0,
  preGeneratedPaymentData,
  paymentMethod = 'qrCode',
  isNonCompany = false,
  nonCompanySummary = null,
  companyBankOnly = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [copied2, setCopied2] = useState(false);
  const [secondPaymentText, setSecondPaymentText] = useState("Placeholder text for second textarea");
  
  // Get the function from the hook directly
  const { createSecondOrderPaymentLink } = useManageOrder();

  // Check if we have pre-generated payment data
  const hasPreGeneratedData = preGeneratedPaymentData;

  // Credit-card detection (same 3.5% Beam fee as OrderAmount). The link data may be
  // nested under .data depending on the caller.
  const CARD_FEE_RATE = 0.035;
  const preData = preGeneratedPaymentData?.data || preGeneratedPaymentData || {};
  const preIsAllCreditCard = preData.isAllCreditCard === true;
  const isGroup1CreditCard = preIsAllCreditCard || preData.isCreditCard === true || paymentMethod === 'creditCard';
  const withCardFee = (amount) => (amount * (1 + CARD_FEE_RATE)).toFixed(2);

  // Set initial text if we have pre-generated data
  useEffect(() => {
    if (hasPreGeneratedData && orderSummary) {
      const text = generateSecondPaymentText(preGeneratedPaymentData, paymentMethod);
      setSecondPaymentText(text);
    }
  }, [preGeneratedPaymentData, orderSummary]);

  // Generate detailed summary text
  const generateSummaryText = () => {
    if (!orderSummary) return '';
    
    // Calculate group totals
    const group1Total = orderSummary.group1Total || 0;
    const group2Total = orderSummary.group2Total || 0;
    
    // Check if both groups have amounts to pay
    const hasGroup1 = group1Total > 0;
    const hasGroup2 = group2Total > 0;
    const hasBothGroups = hasGroup1 && hasGroup2;
    
    let text = `รายละเอียดยอดที่ต้องชำระ${hasBothGroups ? 'ทั้ง 2 ยอด' : ''}\n\n`;

    // Group 1
    if (hasGroup1) {
      if (hasBothGroups) {
        text += '✅ ยอดที่ 1: ค่าสินค้า + ค่าส่งญี่ปุ่น (มัดจำ)\n\n';
      } else {
        text += '✅ ค่าสินค้า + ค่าส่งญี่ปุ่น (มัดจำ)\n\n';
      }
      
      text += `ค่าสินค้าที่ยังไม่ชำระ: ${(orderSummary.notPaidProductPayment || 0).toFixed(2)} บาท\n`;
      text += `ค่าส่งญี่ปุ่น (มัดจำ): ${(orderSummary.notPaidJpDeliveryFeeDown || 0).toFixed(2)} บาท\n`;
      if ((orderSummary.notPaidTransactionFee || 0) > 0) {
        text += `ค่าบริการ: ${(orderSummary.notPaidTransactionFee || 0).toFixed(2)} บาท\n`;
      }
      if ((orderSummary.notPaidExtraJpFees || 0) > 0) {
        text += `ค่าบริการส่งตรวจ: ${(orderSummary.notPaidExtraJpFees || 0).toFixed(2)} บาท\n`;
      }
      text += `\n***ยอดชำระรวม ${group1Total.toFixed(2)} บาท***\n`;
      if (isGroup1CreditCard) {
        text += `ชำระผ่านบัตรเครดิต: ยอดรวม + ค่าธรรมเนียม 3.5% = ${withCardFee(group1Total)} บาท\n`;
      }
    }
    
    // Separator only if both groups exist
    if (hasBothGroups) {
      text += '\n----------------\n\n';
    }
    
    // Group 2
    if (hasGroup2) {
      if (hasBothGroups) {
        text += '✅ ยอดที่ 2: ค่าใช้จ่ายอื่นๆ\n\n';
      } else {
        text += '✅ ค่าส่งและค่าน้ำหนัก\n\n';
      }
      
      text += `ค่าส่งญี่ปุ่น (ไม่มัดจำ): ${(orderSummary.notPaidJpDeliveryFeeNotDown || 0).toFixed(2)} บาท\n`;
      text += `ค่าน้ำหนัก: ${(orderSummary.notPaidWeightFee || 0).toFixed(2)} บาท\n`;
      text += `ค่าส่งไทย: ${(orderSummary.notPaidThaiDeliveryFee || 0).toFixed(2)} บาท\n\n`;
      
      // Apply advance amount deduction only for Group 2
      if (advanceAmount > 0) {
        text += `มียอดฝากไว้: ${formatAmountRound(advanceAmount)} บาท\n`;
        text += `ยอดชำระรวม: ${formatAmountRound(group2Total)} - ${formatAmountRound(advanceAmount)} = ${formatAmountRound(group2Total - advanceAmount)} บาท\n\n`;
        text += `***ยอดชำระรวม ${formatAmountRound(group2Total - advanceAmount)} บาท***\n`;
        if (preIsAllCreditCard) {
          text += `ชำระผ่านบัตรเครดิต: ยอดรวม + ค่าธรรมเนียม 3.5% = ${withCardFee(group2Total - advanceAmount)} บาท\n`;
        }
      } else {
        text += `***ยอดชำระรวม ${group2Total.toFixed(2)} บาท***\n`;
        if (preIsAllCreditCard) {
          text += `ชำระผ่านบัตรเครดิต: ยอดรวม + ค่าธรรมเนียม 3.5% = ${withCardFee(group2Total)} บาท\n`;
        }
      }
    }
    
    return text;
  };

  // Generate second payment text from API response
  const generateSecondPaymentText = (responseData, paymentMethod = 'qrCode') => {
    if (!responseData || !orderSummary) return "Placeholder text for second textarea";
    
    // Normalize response data structure - handle both single and double level responses
    const normalizedData = responseData.data || responseData;
    
    // Check if all payments are credit card (when allCreditCard is true)
    const isAllCreditCard = normalizedData.isAllCreditCard === true;
    // Payment links are off (backend flag, or caller without pre-generated data)
    const group1ByCompanyBank = companyBankOnly || normalizedData.isBankTransferOnly === true;
    
    // Calculate group totals
    const group1Total = orderSummary.group1Total || 0;
    const group2Total = orderSummary.group2Total || 0;
    
    // Check if both groups have amounts to pay
    const hasGroup1 = group1Total > 0;
    const hasGroup2 = group2Total > 0;
    const hasBothGroups = hasGroup1 && hasGroup2;
    
    let text = '';
    
    // Group 1
    const group1IsCard = isAllCreditCard || normalizedData.isCreditCard === true || paymentMethod === 'creditCard';
    if (hasGroup1) {
      if (hasBothGroups) {
        text += `✅ ยอดที่ 1: **ยอดชำระรวม ${group1Total.toFixed(2)} บาท**\n\n`;
      } else {
        text += `**ยอดชำระรวม ${group1Total.toFixed(2)} บาท**\n\n`;
      }
      if (group1IsCard) {
        text += `ยอดรวม + ค่าธรรมเนียม 3.5% = ${withCardFee(group1Total)} บาท\n\n`;
      }
      if (group1ByCompanyBank) {
        text += `${companyBankText()}\n\n`;
      } else {
        text += 'ชำระผ่านลิ้งค์ ด้านล่างได้เลยครับ\n';
        // Use group1PaymentUrl if allCreditCard is true, otherwise use paymentUrl
        const group1Url = isAllCreditCard ? (normalizedData.group1PaymentUrl || 'ไม่พบลิ้งค์การชำระ') : (normalizedData.paymentUrl || 'ไม่พบลิ้งค์การชำระ');
        text += `${group1Url}\n\n`;
      }
    }
    
    // Group 2
    if (hasGroup2) {
      if (hasBothGroups) {
        // Apply advance amount deduction only for Group 2
        if (advanceAmount > 0) {
          text += `✅ ยอดที่ 2: **ยอดชำระรวม ${formatAmountRound(group2Total)} - ${formatAmountRound(advanceAmount)} = ${formatAmountRound(group2Total - advanceAmount)} บาท**\n\n`;
        } else {
          text += `✅ ยอดที่ 2: **ยอดชำระรวม ${group2Total.toFixed(2)} บาท**\n\n`;
        }
        if (isAllCreditCard) {
          text += `ยอดรวม + ค่าธรรมเนียม 3.5% = ${withCardFee(advanceAmount > 0 ? group2Total - advanceAmount : group2Total)} บาท\n\n`;
        }
      } else {
        // Apply advance amount deduction only for Group 2
        if (advanceAmount > 0) {
          text += `**ยอดชำระรวม ${formatAmountRound(group2Total)} - ${formatAmountRound(advanceAmount)} = ${formatAmountRound(group2Total - advanceAmount)} บาท**\n\n`;
        } else {
          text += `**ยอดชำระรวม ${group2Total.toFixed(2)} บาท**\n\n`;
        }
        if (isAllCreditCard) {
          text += `ยอดรวม + ค่าธรรมเนียม 3.5% = ${withCardFee(advanceAmount > 0 ? group2Total - advanceAmount : group2Total)} บาท\n\n`;
        }
      }
      
      // If allCreditCard is true, show credit card link, otherwise show bank transfer info
      if (isAllCreditCard) {
        text += 'ชำระผ่านลิ้งค์ ด้านล่างได้เลยครับ\n';
        text += `${normalizedData.group2PaymentUrl || 'ไม่พบลิ้งค์การชำระ'}\n\n`;
      } else {
        text += 'ชำระผ่านโอนบัญชีธนาคารด้านล่างได้เลยครับ\n\n';
        text += `${normalizedData.bankInfo?.bankName || 'ไม่พบข้อมูลธนาคาร'}\n`;
        text += `เลขบัญชี ${normalizedData.bankInfo?.accountNumber || 'ไม่พบเลขบัญชี'}\n`;
        text += `ชื่อบัญชี ${normalizedData.bankInfo?.accountNameTh || 'ไม่พบชื่อบัญชี'}\n`;
        if(!hasBothGroups)
          text += '\nหลังชำระแล้วรบกวนส่งสลิป ยืนยันด้วยนะครับ';
      }
    }

    if (hasBothGroups) {
      text += '\n---------------------\n';
      text += '🔥🔥 รบกวนชำระทั้ง 2 ช่องทาง 🔥🔥\n';
      text += 'หากชำระครบแล้วรบกวนแจ้งด้วยนะครับ\n';
    }
    
    return text;
  };

  // Copy summary to clipboard
  const copySummaryToClipboard = async () => {
    try {
      const summaryText = generateSummaryText();
      await navigator.clipboard.writeText(summaryText);
      toast.success('คัดลอกรายละเอียดยอดชำระแล้ว');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error('ไม่สามารถคัดลอกได้');
    }
  };

  // Copy second text to clipboard
  const copySecondText = async () => {
    try {
      // If we have pre-generated data, just copy it
      if (hasPreGeneratedData) {
        const text = generateSecondPaymentText(preGeneratedPaymentData);
        await navigator.clipboard.writeText(text);
        toast.success('คัดลอกข้อความการชำระแล้ว');
        setCopied2(true);
        setTimeout(() => setCopied2(false), 2000);
        return;
      }

      // Payment links are off: group 1 goes to the company account, group 2 keeps the usual bank
      if (companyBankOnly) {
        const bankResponse = await DebtFollowUpService.getBankInfo(orderSummary?.group2Total || 0);
        const text = generateSecondPaymentText({ bankInfo: bankResponse.data?.bankInfo });
        setSecondPaymentText(text);
        await navigator.clipboard.writeText(text);
        toast.success('คัดลอกข้อความการชำระแล้ว');
        setCopied2(true);
        setTimeout(() => setCopied2(false), 2000);
        return;
      }

      // Original logic for creating payment link when no pre-generated data
      const urls = orders.map(order => order.url);
      const bankAmount = orderSummary?.group2Total || 0;
      
      const secondPaymentResponse = await createSecondOrderPaymentLink({
        smileName: smileName,
        urls: urls,
        bankAmount: bankAmount
      });
      console.log(secondPaymentResponse)

      const generatedText = generateSecondPaymentText(secondPaymentResponse);
      setSecondPaymentText(generatedText);
      
      await navigator.clipboard.writeText(generatedText);
      toast.success('คัดลอกข้อความการชำระแล้ว');
      setCopied2(true);
      setTimeout(() => setCopied2(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      toast.error('ไม่สามารถคัดลอกได้');
    }
  };

  if (isNonCompany && nonCompanySummary) {
    const net = Math.max(0, nonCompanySummary.totalAmount - advanceAmount);
    const bankInfo = nonCompanySummary.bankInfo;

    let paymentText = `รายละเอียดยอดที่ต้องชำระ (บิล ${nonCompanySummary.code})\n\n`;
    if (advanceAmount > 0) {
      paymentText += `ยอดรวม: ${formatAmountRound(nonCompanySummary.totalAmount)} บาท\n`;
      paymentText += `มียอดฝากไว้: ${formatAmountRound(advanceAmount)} บาท\n`;
      paymentText += `***ยอดชำระรวม ${formatAmountRound(net)} บาท***\n\n`;
    } else {
      paymentText += `***ยอดชำระรวม ${formatAmountRound(nonCompanySummary.totalAmount)} บาท***\n\n`;
    }
    paymentText += `ชำระผ่านโอนบัญชีธนาคารด้านล่างได้เลยครับ\n\n`;
    paymentText += `${bankInfo?.bankName || 'ไม่พบข้อมูลธนาคาร'}\n`;
    paymentText += `เลขบัญชี ${bankInfo?.accountNumber || 'ไม่พบเลขบัญชี'}\n`;
    paymentText += `ชื่อบัญชี ${bankInfo?.accountNameTh || 'ไม่พบชื่อบัญชี'}\n\n`;
    paymentText += `หลังชำระแล้วรบกวนส่งสลิป ยืนยันด้วยนะครับ`;

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(paymentText);
        toast.success('คัดลอกข้อความการชำระแล้ว');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('ไม่สามารถคัดลอกได้');
      }
    };

    return (
      <div className='space-y-2'>
        <div className='flex gap-2'>
          <textarea
            value={paymentText}
            readOnly
            className='flex-1 p-3 border border-gray-300 rounded-lg resize-none text-base'
            rows={15}
          />
          <div className='flex flex-col gap-2 flex-shrink-0'>
            <button
              onClick={handleCopy}
              className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2 h-fit'
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </button>
            {copied && (
              <div className='text-green-800 text-xl font-medium text-center'>
                Copied!
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      <div className='flex gap-4'>
        {/* First Textarea */}
        <div className='flex gap-2 flex-1'>
          <textarea
            value={generateSummaryText()}
            readOnly
            className='flex-1 p-3 border border-gray-300 rounded-lg resize-none text-base overflow-hidden'
            style={{ height: 'auto', minHeight: '120px' }}
            rows={19}
          />
          <div className='flex flex-col gap-2 flex-shrink-0'>
            <button
              onClick={copySummaryToClipboard}
              className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2 h-fit'
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </button>
            {copied && (
              <div className='text-green-800 text-xl font-medium text-center'>
                Copied!
              </div>
            )}
          </div>
        </div>

        {/* Second Textarea */}
        <div className='flex gap-2 flex-1'>
          <textarea
            value={secondPaymentText}
            readOnly
            className='flex-1 p-3 border border-gray-300 rounded-lg resize-none text-base overflow-hidden'
            style={{ height: 'auto', minHeight: '120px' }}
            rows={19}
          />
          <div className='flex flex-col gap-2 flex-shrink-0'>
            <button
              onClick={copySecondText}
              className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2 h-fit'
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {hasPreGeneratedData || companyBankOnly ? "Copy" : "ขอลิ้งค์"}
            </button>
            {copied2 && (
              <div className='text-green-800 text-xl font-medium text-center'>
                Copied!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTextArea;
